/**
 * Anki SM-2 variant algorithm implementation
 * 
 * Based on Anki's spaced repetition algorithm, which is a variant of SuperMemo SM-2.
 * 
 * Key differences from SuperMemo SM-2:
 * - Anki uses a different ease factor adjustment formula
 * - Anki has different interval multipliers
 * - Anki's minimum ease factor is 1.3 (same as SM-2)
 */

export interface AnkiSchedule {
  easeFactor: number
  interval: number
  repetitions: number
}

export interface AnkiReviewResult extends AnkiSchedule {
  dueDate: number // Unix timestamp
}

/**
 * Calculate new Anki schedule based on grade
 * 
 * @param grade - Review grade (0-5)
 * @param currentEaseFactor - Current ease factor (default 2.5)
 * @param currentInterval - Current interval in days
 * @param currentRepetitions - Current repetition count
 * @returns New schedule with updated values
 */
export function calculateAnkiSchedule(
  grade: 0 | 1 | 2 | 3 | 4 | 5,
  currentEaseFactor: number,
  currentInterval: number,
  currentRepetitions: number
): AnkiReviewResult {
  const MIN_EASE_FACTOR = 1.3
  const msPerDay = 24 * 60 * 60 * 1000
  const now = Date.now()

  let newEaseFactor = currentEaseFactor
  let newInterval = currentInterval
  let newRepetitions = currentRepetitions

  // Anki's ease factor adjustment formula
  // EF' = EF + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02))
  // where q is the quality (grade)
  const quality = grade
  const easeChange = 0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)
  newEaseFactor = Math.max(MIN_EASE_FACTOR, currentEaseFactor + easeChange)

  // Round ease factor to 2 decimal places
  newEaseFactor = Math.round(newEaseFactor * 100) / 100

  if (grade < 3) {
    // Grade 0-2: Reset to beginning
    newInterval = 1
    newRepetitions = 0
  } else if (grade === 3) {
    // Grade 3: Small interval increase
    if (currentRepetitions === 0) {
      newInterval = 1
    } else if (currentRepetitions === 1) {
      newInterval = 6
    } else {
      newInterval = Math.ceil(currentInterval * newEaseFactor)
    }
    newRepetitions = currentRepetitions + 1
  } else {
    // Grade 4-5: Larger interval increase
    if (currentRepetitions === 0) {
      newInterval = 1
    } else if (currentRepetitions === 1) {
      newInterval = 6
    } else {
      newInterval = Math.ceil(currentInterval * newEaseFactor)
    }
    newRepetitions = currentRepetitions + 1
  }

  // Calculate new due date
  const dueDate = now + newInterval * msPerDay

  return {
    easeFactor: newEaseFactor,
    interval: newInterval,
    repetitions: newRepetitions,
    dueDate,
  }
}

/**
 * Initialize new flashcard with default Anki values
 */
export function initializeAnkiSchedule(): AnkiSchedule {
  return {
    easeFactor: 2.5,
    interval: 0, // Due immediately
    repetitions: 0,
  }
}

